import { memo, useState, type KeyboardEvent, type RefObject } from 'react'
import { ChevronDown, ListOrdered } from 'lucide-react'

import { Sortable } from '@/components/custom/sortable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { TooltipProvider } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  EMPTY_QUEUE,
  useMessageQueue,
  type QueuedMessage,
} from '@/stores/messageQueue'

import { QueuedMessageRow } from './QueuedMessageRow'
import { useListPresence } from './useListPresence'

interface QueuedMessagesControlProps {
  conversationId: string
  isRunActive: boolean
  onPromote: (item: QueuedMessage) => void
  onEdit: (item: QueuedMessage) => void
  onCancelEdit: () => void
  /** Takes focus back when the row button the user pressed goes away. */
  composerRef: RefObject<HTMLTextAreaElement | null>
}

// The grip's own key handler replaces dnd-kit's, so Space does not pick a
// row up; the announced instructions must say what works.
const SORTABLE_A11Y = {
  screenReaderInstructions: {
    draggable:
      'To move this message, press the up or down arrow key, or drag it with the pointer.',
  },
}

/** Messages waiting behind the active run, docked on top of the composer. */
export const QueuedMessagesControl = memo(function QueuedMessagesControl({
  conversationId,
  isRunActive,
  onPromote,
  onEdit,
  onCancelEdit,
  composerRef,
}: QueuedMessagesControlProps) {
  const { items, editingId } =
    useMessageQueue((s) => s.queues[conversationId]) ?? EMPTY_QUEUE
  const [expanded, setExpanded] = useState(true)
  // A collapsed list has no rows on screen to animate.
  const { rows, settle, settleAll } = useListPresence(items, expanded)
  // The count outlives the queue so the banner can close on its last number.
  const [banner, setBanner] = useState({ count: items.length, enter: false })
  if (items.length > 0 && items.length !== banner.count) {
    setBanner({ count: items.length, enter: true })
  }

  if (banner.count === 0) return null
  const open = items.length > 0

  const { reorder, remove } = useMessageQueue.getState()

  const removeItem = (item: QueuedMessage) => {
    item.attachments.forEach((a) => URL.revokeObjectURL(a.url))
    remove(conversationId, item.id)
    composerRef.current?.focus()
  }

  const promoteItem = (item: QueuedMessage) => {
    onPromote(item)
    composerRef.current?.focus()
  }

  const moveWithKeys = (
    e: KeyboardEvent<HTMLButtonElement>,
    item: QueuedMessage,
    index: number
  ) => {
    const to =
      e.key === 'ArrowUp' ? index - 1 : e.key === 'ArrowDown' ? index + 1 : -1
    if (to < 0 || to >= items.length) return
    e.preventDefault()
    reorder(conversationId, item.id, to)
  }

  return (
    <TooltipProvider>
      {/* Easing the row height keeps the composer still while the list above
          it makes room. */}
      <div
        data-state={open ? 'open' : 'closed'}
        data-entering={banner.enter || undefined}
        inert={!open}
        aria-hidden={!open || undefined}
        onTransitionEnd={(e) => {
          if (!open && e.target === e.currentTarget) {
            setBanner({ count: 0, enter: false })
            settleAll()
          }
        }}
        className="group/banner grid grid-rows-[1fr] transition-[grid-template-rows] duration-fast ease-smooth-out data-[state=closed]:grid-rows-[0fr] data-[state=closed]:duration-quick data-entering:starting:grid-rows-[0fr]">
        <div className="flex min-h-0 flex-col justify-end overflow-hidden">
          <Collapsible asChild open={expanded} onOpenChange={setExpanded}>
            <section
              aria-label={`${banner.count} queued message${banner.count === 1 ? '' : 's'}`}
              className="mx-3 rounded-t-lg border border-b-0 bg-background/70 p-1 text-xs backdrop-blur-md transition-[translate,opacity] duration-fast ease-smooth-out group-data-[state=closed]/banner:translate-y-2 group-data-[state=closed]/banner:opacity-0 group-data-[state=closed]/banner:duration-quick group-data-entering/banner:starting:translate-y-2 group-data-entering/banner:starting:opacity-0">
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={
                    expanded
                      ? 'Collapse queued messages'
                      : 'Expand queued messages'
                  }
                  className="h-7 w-full justify-start px-1.5 text-xs font-normal text-muted-foreground">
                  <ListOrdered />
                  <span>Queued</span>
                  <Badge variant="secondary" className="ml-auto px-1.5 py-0">
                    <span
                      key={banner.count}
                      className="transition-[opacity,translate,filter] duration-quick ease-smooth-out starting:translate-y-1 starting:opacity-0 starting:blur-[2px]">
                      {banner.count}
                    </span>
                  </Badge>
                  <ChevronDown
                    className={cn(
                      'transition-transform duration-fast ease-smooth-out',
                      !expanded && '-rotate-90'
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
              <Sortable
                value={items}
                accessibility={SORTABLE_A11Y}
                onMove={({ activeIndex, overIndex }) => {
                  const moved = items[activeIndex]
                  // A collapsing row is still a drop target, but not a slot.
                  if (moved && overIndex >= 0)
                    reorder(conversationId, moved.id, overIndex)
                }}>
                <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                  <ol className="max-h-32 overflow-y-auto">
                    {rows.map(({ item, presence }) => {
                      const index = items.indexOf(item)
                      return (
                        <QueuedMessageRow
                          key={item.id}
                          item={item}
                          presence={presence}
                          onExited={() => settle(item.id)}
                          position={`${index + 1} of ${items.length}`}
                          isEditing={item.id === editingId}
                          editLocked={editingId !== null}
                          isRunActive={isRunActive}
                          onMoveKey={(e) => moveWithKeys(e, item, index)}
                          onEdit={() => onEdit(item)}
                          onCancelEdit={onCancelEdit}
                          onPromote={() => promoteItem(item)}
                          onRemove={() => removeItem(item)}
                        />
                      )
                    })}
                  </ol>
                </CollapsibleContent>
              </Sortable>
            </section>
          </Collapsible>
        </div>
      </div>
    </TooltipProvider>
  )
})
